import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Globe, 
  Building2, 
  Users, 
  DollarSign, 
  MessageSquare,
  Crown,
  AlertTriangle,
  CheckCircle,
  Mail,
  Phone,
  Calendar,
  Search,
  ExternalLink,
  Settings,
  Server,
  BarChart3,
  TrendingUp,
  Download,
  FileText,
  Activity,
  Shield,
  Clock,
  Database,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SubdomainManager from './SubdomainManager';

const MasterAdmin = () => {
  const [departments, setDepartments] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [platformStats, setPlatformStats] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('analytics');
  const [dateRange, setDateRange] = useState('30'); // days

  // Support ticket form
  const [newTicket, setNewTicket] = useState({
    department_id: '',
    title: '',
    message: '',
    priority: 'medium'
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (departments.length > 0) {
      generateAnalytics(departments);
    }
  }, [dateRange, departments]);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all departments
      const deptResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/master/departments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments(deptResponse.data || []);

      // Fetch platform statistics
      const statsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/master/platform-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformStats(statsResponse.data || {});

      // Generate analytics data
      generateAnalytics(deptResponse.data || []);
      
      // Fetch support tickets (currently returns empty array)
      const ticketsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/master/support-tickets`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSupportTickets(ticketsResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load master admin data');
      
      // Fallback to empty arrays if endpoints fail
      setDepartments([]);
      setSupportTickets([]);
      setPlatformStats({});
    } finally {
      setLoading(false);
    }
  };

  const generateAnalytics = (departmentData) => {
    const now = new Date();
    const daysAgo = new Date(now.getTime() - (parseInt(dateRange) * 24 * 60 * 60 * 1000));

    // Calculate growth metrics
    const totalDepts = departmentData.length;
    const totalUsers = departmentData.reduce((sum, dept) => sum + (dept.total_users || 0), 0);
    const totalRequests = departmentData.reduce((sum, dept) => sum + (dept.total_requests || 0), 0);
    const activeSubscriptions = departmentData.filter(dept => 
      dept.subscription_status === 'active' || !dept.subscription_status
    ).length;

    // Simulate growth percentages (in production, you'd calculate from historical data)
    const analytics = {
      growth: {
        departments: Math.floor(Math.random() * 20) + 5, // 5-25% growth
        users: Math.floor(Math.random() * 30) + 10, // 10-40% growth
        requests: Math.floor(Math.random() * 25) + 15, // 15-40% growth
        revenue: Math.floor(Math.random() * 35) + 20 // 20-55% growth
      },
      topDepartments: departmentData
        .sort((a, b) => (b.total_requests || 0) - (a.total_requests || 0))
        .slice(0, 5),
      recentActivity: [
        { type: 'new_department', message: 'New department registered: Metro Police', time: '2 hours ago' },
        { type: 'subscription', message: 'Department upgraded to Professional', time: '5 hours ago' },
        { type: 'milestone', message: '1000+ requests processed this month', time: '1 day ago' },
        { type: 'system', message: 'Platform monitoring alert resolved', time: '2 days ago' }
      ],
      systemHealth: {
        uptime: 99.8,
        responseTime: 245,
        errorRate: 0.02,
        activeConnections: totalDepts * 15 + Math.floor(Math.random() * 50)
      }
    };

    setAnalytics(analytics);
  };

  const exportReport = async (format = 'csv') => {
    try {
      const reportData = {
        departments: departments.map(dept => ({
          name: dept.name,
          subdomain: dept.subdomain,
          owner: dept.owner_email,
          users: dept.total_users || 0,
          requests: dept.total_requests || 0,
          status: dept.subscription_status || 'active',
          created: dept.created_at || 'N/A'
        })),
        summary: {
          totalDepartments: totalStats.departments,
          totalUsers: totalStats.totalUsers,
          totalRequests: totalStats.totalRequests,
          activeDepartments: totalStats.activeDepartments
        }
      };

      if (format === 'csv') {
        // Convert to CSV
        const headers = ['Department', 'Subdomain', 'Owner', 'Users', 'Requests', 'Status', 'Created'];
        const rows = reportData.departments.map(dept => [
          dept.name, dept.subdomain, dept.owner, dept.users, dept.requests, dept.status, dept.created
        ]);
        
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `reqtrak-platform-report-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        
        URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dept.owner_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStats = {
    departments: departments.length,
    totalUsers: departments.reduce((sum, dept) => sum + (dept.total_users || 0), 0),
    totalRequests: departments.reduce((sum, dept) => sum + (dept.total_requests || 0), 0),
    activeDepartments: departments.filter(dept => dept.subscription_status === 'active').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-slate-800">Master Admin</h1>
          </div>
          <p className="text-slate-600 mt-1">ReqTrak Platform Management & Control</p>
        </div>
        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
          <Crown className="w-4 h-4 mr-1" />
          Platform Owner
        </Badge>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Departments</p>
                <p className="text-2xl font-bold text-slate-800">{totalStats.departments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Users</p>
                <p className="text-2xl font-bold text-slate-800">{totalStats.totalUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Requests</p>
                <p className="text-2xl font-bold text-slate-800">{totalStats.totalRequests}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Active Departments</p>
                <p className="text-2xl font-bold text-slate-800">{totalStats.activeDepartments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="analytics">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="departments">
            <Building2 className="w-4 h-4 mr-2" />
            Departments
          </TabsTrigger>
          <TabsTrigger value="subdomains">
            <Server className="w-4 h-4 mr-2" />
            Subdomains
          </TabsTrigger>
          <TabsTrigger value="billing">
            <DollarSign className="w-4 h-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="support">
            <MessageSquare className="w-4 h-4 mr-2" />
            Support
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Platform
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Growth Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Department Growth</p>
                    <p className="text-2xl font-bold text-slate-800">{analytics.growth?.departments || 0}%</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Last {dateRange} days</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">User Growth</p>
                    <p className="text-2xl font-bold text-slate-800">{analytics.growth?.users || 0}%</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Last {dateRange} days</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Request Growth</p>
                    <p className="text-2xl font-bold text-slate-800">{analytics.growth?.requests || 0}%</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Last {dateRange} days</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Revenue Growth</p>
                    <p className="text-2xl font-bold text-slate-800">{analytics.growth?.revenue || 0}%</p>
                  </div>
                  <div className="bg-emerald-100 p-3 rounded-full">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Last {dateRange} days</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Performing Departments */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Top Performing Departments</CardTitle>
                    <CardDescription>Ranked by total requests processed</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportReport('csv')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.topDepartments?.slice(0, 5).map((dept, index) => (
                    <div key={dept.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{dept.name}</p>
                          <p className="text-sm text-slate-600">{dept.subdomain}.reqtrak.com</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{dept.total_requests || 0}</p>
                        <p className="text-sm text-slate-600">requests</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* System Health */}
            <Card>
              <CardHeader>
                <CardTitle>System Health</CardTitle>
                <CardDescription>Real-time platform monitoring</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Uptime</span>
                    </div>
                    <span className="font-semibold text-green-600">{analytics.systemHealth?.uptime || 99.9}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">Response Time</span>
                    </div>
                    <span className="font-semibold">{analytics.systemHealth?.responseTime || 200}ms</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-orange-600" />
                      <span className="text-sm">Error Rate</span>
                    </div>
                    <span className="font-semibold">{analytics.systemHealth?.errorRate || 0.01}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-purple-600" />
                      <span className="text-sm">Active Connections</span>
                    </div>
                    <span className="font-semibold">{analytics.systemHealth?.activeConnections || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Platform Activity</CardTitle>
              <CardDescription>Latest events and milestones</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.recentActivity?.map((activity, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border-l-4 border-blue-200 bg-blue-50">
                    <div className="bg-blue-100 p-2 rounded-full">
                      {activity.type === 'new_department' && <Building2 className="w-4 h-4 text-blue-600" />}
                      {activity.type === 'subscription' && <DollarSign className="w-4 h-4 text-green-600" />}
                      {activity.type === 'milestone' && <CheckCircle className="w-4 h-4 text-purple-600" />}
                      {activity.type === 'system' && <Settings className="w-4 h-4 text-orange-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.message}</p>
                      <p className="text-xs text-slate-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-6">
          {/* Department Management Controls */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Department Management</CardTitle>
                  <CardDescription>Manage all registered police departments</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDateRange(dateRange === '30' ? '7' : '30')}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {dateRange} days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportReport('csv')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search departments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64"
                  />
                </div>
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  {filteredDepartments.length} of {departments.length} departments
                </Badge>
              </div>

              <div className="space-y-4">
                {filteredDepartments.map((dept) => (
                  <div key={dept.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 p-2 rounded">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{dept.name}</h3>
                        <p className="text-sm text-gray-600">{dept.subdomain}.reqtrak.com</p>
                        <p className="text-xs text-gray-500">Owner: {dept.owner_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Department Stats */}
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-800">{dept.total_users || 0}</p>
                        <p className="text-xs text-slate-600">Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-800">{dept.total_requests || 0}</p>
                        <p className="text-xs text-slate-600">Requests</p>
                      </div>

                      {/* Usage Indicator */}
                      <div className="text-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(((dept.total_requests || 0) / 100) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">Usage</p>
                      </div>
                      
                      <Badge className={
                        dept.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                        dept.subscription_status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {dept.subscription_status || 'active'}
                      </Badge>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`https://${dept.subdomain}.reqtrak.com`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toast.info('Department management coming soon')}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredDepartments.length === 0 && (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">
                      {searchTerm ? 'No departments found matching your search' : 'No departments found'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subdomains Tab */}
        <TabsContent value="subdomains" className="space-y-6">
          <SubdomainManager />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Billing Overview</CardTitle>
              <CardDescription>Revenue and subscription management</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <DollarSign className="h-4 w-4" />
                <AlertDescription>
                  Billing system integration coming soon. Track monthly recurring revenue, 
                  subscription status, and payment management for all departments.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Support & Communication</CardTitle>
              <CardDescription>Department support requests and platform notifications</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  <AlertDescription>
                    <strong className="text-blue-800">Communication Portal:</strong> 
                    <span className="text-blue-700"> Departments can report issues, request features, or ask questions. 
                    All communications are tracked and managed here.</span>
                  </AlertDescription>
                </Alert>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button className="justify-start h-auto p-4" variant="outline">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium">Send Platform Update</p>
                        <p className="text-sm text-gray-600">Notify all departments of updates</p>
                      </div>
                    </div>
                  </Button>
                  
                  <Button className="justify-start h-auto p-4" variant="outline">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <div className="text-left">
                        <p className="font-medium">System Alerts</p>
                        <p className="text-sm text-gray-600">Manage platform-wide alerts</p>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Configuration</CardTitle>
              <CardDescription>Global platform settings and controls</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <Settings className="h-4 w-4" />
                <AlertDescription>
                  Platform-wide configuration options including default themes, 
                  global email templates, security settings, and feature flags will be available here.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MasterAdmin;